/**
 * Picks an icon name for an entity, room or device. Pure data and logic, no
 * React, so scripts/icon-coverage.ts can run it under tsx.
 *
 * iconFor() resolution order (first hit wins), then a state pass:
 *   1. admin override: meta.icon in the curated namespace, "eh:<name>"
 *   2. HA icon (registry icon / original_icon / state attribute) via the mdi table
 *   2b. lights (and switches that drive lights): name keywords → fitting type
 *       (lib/icons/lights.ts); a bare mdi:lightbulb doesn't stop this step
 *   3. device_class (+ unit and entity-id keywords for sensors)
 *   4. domain
 *   5. device: device.icon override, then device.type
 *   6. generic fallback ('generic')
 * The state pass then swaps a base glyph for its stateful variant: door/window
 * open, lock jammed, blind position, battery level, light/plug on, fan
 * spinning, heating/cooling, leak wet, motion detected, Wi-Fi strength, alarm.
 */
import type { Device, DeviceType, EntityMeta, EntityState, Room } from '@home/shared';
import { MDI } from './mdi.js';
import { hasIcon } from './registry.js';
import { LIGHT_ICONS, lightFromName } from './lights.js';
import { CONDITION_ICON } from '../weather.js';

/** Curated-icon namespace for admin overrides stored where HA keeps `mdi:` names. */
export const ICON_NS = 'eh:';
export const FALLBACK_ICON = 'generic';

/** Resolution step that produced an icon; the coverage script reports on it. */
export type IconSource = 'override' | 'mdi' | 'name' | 'device_class' | 'domain' | 'device' | 'domain_default' | 'fallback';

const curated = (v: string | null | undefined): string | undefined => {
  if (!v || !v.startsWith(ICON_NS)) return undefined;
  const k = v.slice(ICON_NS.length);
  return hasIcon(k) ? k : undefined;
};

/** Maps an HA icon ("mdi:…", or a curated "eh:…") to a curated icon name. */
export function mdiToIcon(mdi: string | null | undefined): string | undefined {
  if (!mdi) return undefined;
  const own = curated(mdi);
  if (own) return own;
  const name = mdi.replace(/^mdi:/, '');
  if (name in MDI) return MDI[name] ?? undefined;
  const base = name.replace(/-(outline|off|variant|alert|circle|box|minus|plus|on|auto|check|remove|outline-off)$/g, '');
  if (base in MDI) return MDI[base] ?? undefined;
  const first = name.split('-')[0] ?? '';
  return MDI[first] ?? undefined;
}

// ---------------------------------------------------------------- device_class tables

const SENSOR_DC: Record<string, string> = {
  temperature: 'thermometer', humidity: 'humidity', moisture: 'humidity', power: 'bolt', apparent_power: 'bolt',
  reactive_power: 'bolt', energy: 'meter', energy_storage: 'battery', energy_distance: 'ev-charger',
  current: 'current', voltage: 'voltage', power_factor: 'gauge', frequency: 'current', battery: 'battery',
  carbon_dioxide: 'co2', carbon_monoxide: 'molecule', volatile_organic_compounds: 'molecule',
  volatile_organic_compounds_parts: 'molecule', nitrogen_dioxide: 'molecule', ozone: 'molecule',
  pm25: 'filter', pm10: 'filter', pm1: 'filter', aqi: 'filter', pressure: 'pressure',
  atmospheric_pressure: 'pressure', illuminance: 'lux', irradiance: 'solar', signal_strength: 'signal',
  data_rate: 'wifi', data_size: 'storage', timestamp: 'clock', date: 'calendar', duration: 'timer',
  monetary: 'currency', wind_speed: 'wind', wind_direction: 'compass', speed: 'gauge', precipitation: 'rain',
  precipitation_intensity: 'rain', water: 'droplet', gas: 'flame', distance: 'pin', weight: 'gauge', ph: 'flask',
  temperature_delta: 'thermometer', volume: 'droplet', volume_storage: 'droplet', volume_flow_rate: 'pump',
  sound_pressure: 'volume', enum: 'list',
};

/** binary_sensor device_class → [off, on]. */
const BINARY_DC: Record<string, [string, string]> = {
  door: ['door', 'door-open'], garage_door: ['garage', 'garage-open'], window: ['window', 'window-open'],
  opening: ['door', 'door-open'], motion: ['motion', 'motion-on'], occupancy: ['occupancy', 'occupancy-on'],
  presence: ['occupancy', 'occupancy-on'], moisture: ['leak-dry', 'leak'], smoke: ['smoke', 'smoke-on'],
  gas: ['gas', 'gas'], carbon_monoxide: ['molecule', 'molecule'], heat: ['flame', 'flame'],
  cold: ['snowflake', 'snowflake'], battery: ['battery', 'battery-low'], battery_charging: ['battery', 'battery-charging'],
  connectivity: ['wifi-off', 'wifi'], lock: ['lock', 'unlock'], plug: ['plug', 'plug-on'], power: ['power', 'bolt'],
  problem: ['shield-check', 'alert'], safety: ['shield-check', 'alert'], tamper: ['shield-check', 'alert'],
  light: ['moon', 'sun'], sound: ['volume', 'volume'], vibration: ['event', 'event'], running: ['power', 'fan-on'],
  update: ['update', 'update-on'],
};

const COVER_DC: Record<string, string> = {
  garage: 'garage', gate: 'gate', door: 'door', window: 'window', blind: 'blind', shade: 'blind',
  curtain: 'curtain', shutter: 'shutter', awning: 'shutter', damper: 'damper',
};

const DOMAIN: Record<string, string> = {
  light: 'bulb', switch: 'switch', input_boolean: 'toggle', fan: 'fan', cover: 'blind', climate: 'thermostat',
  media_player: 'speaker', lock: 'lock', alarm_control_panel: 'shield', camera: 'camera', scene: 'scene',
  script: 'script', button: 'button', input_button: 'button', input_number: 'number', number: 'number',
  input_select: 'select', select: 'select', input_text: 'input', text: 'input', input_datetime: 'calendar',
  datetime: 'calendar', date: 'calendar', time: 'clock', schedule: 'schedule', timer: 'timer', counter: 'hash',
  vacuum: 'vacuum', water_heater: 'water-heater', valve: 'valve', siren: 'siren', update: 'update', event: 'event',
  weather: 'cloud-sun', person: 'person', device_tracker: 'pin', sun: 'sun', humidifier: 'humidity', remote: 'remote',
  automation: 'robot', zone: 'pin', image: 'camera', lawn_mower: 'robot', notify: 'bell', tts: 'speaker',
  conversation: 'microphone', calendar: 'calendar', todo: 'list', stt: 'microphone', wake_word: 'microphone',
  assist_satellite: 'microphone', tag: 'key', ai_task: 'sparkle',
};

/** Domains whose own icon is only a last resort (they say nothing about what is measured). */
const WEAK_DOMAIN: Record<string, string> = { sensor: 'gauge', binary_sensor: 'event' };

const WEATHER: Readonly<Record<string, string>> = CONDITION_ICON;

const DEVICE_TYPE: Record<DeviceType, string> = {
  thermostat: 'thermostat', aircon: 'ac', light: 'bulb', cover: 'blind', energy: 'inverter', ev: 'ev-charger',
  hottub: 'hot-tub', security: 'shield', door: 'door', network: 'router', media: 'speaker', sensor: 'gauge',
  appliance: 'washer', vehicle: 'car', helper: 'robot', other: 'box',
};

/** Icon for a functional device type (Settings page, device lists). */
export function deviceTypeIcon(type: DeviceType | string | null | undefined): string {
  return (type && DEVICE_TYPE[type as DeviceType]) || 'box';
}

/** Entity-id / name keywords → icon, for sensors with no device_class. Order matters. */
const KEYWORDS: Array<[RegExp, string]> = [
  [/(^|_)unknown_|control_id|writes_refused|serial_number|_mac$|uplink|number_of_sectors/, 'chip'],
  // Before "cycle" (battery) and "program" (washer): the dishwasher's cycle phase, programme and options.
  [/dishwasher/, 'dishwasher'],
  [/skylight/, 'skylight'],
  [/damper/, 'damper'],
  [/setpoint|standby|hold_active|floor_limit|_away$/, 'thermostat'],
  [/(^|_)ph(_|$)|electrode|chlorine|bromine/, 'flask'],
  [/(^|_)bin(_|$)/, 'bin'],
  [/cycle|bms|_cell/, 'battery'],
  [/parking_brake|remote_access|range_miles|electic_range|electric_range|_range$/, 'car'],
  [/cleaning|braava|saros|roomba|mop/, 'vacuum'],
  [/print|nozzle|layer_count|printable|active_tray/, 'printer'],
  [/program|remote_start|local_control|remote_control|washing|dryer/, 'washer'],
  [/audio_input|microphone/, 'microphone'],
  [/bright_outside|daylight/, 'lux'],
  [/flame|demand/, 'flame'],
  [/defrost/, 'snowflake'],
  [/nearest_device/, 'pin'],
  [/myip|opensky|public_ip|wan_/, 'globe'],
  [/swap|usage/, 'storage'],
  [/tempo|song|track|media/, 'music'],
  [/relay|repeater|output/, 'switch'],
  [/(^|_)pir|tag_scanned/, 'motion'],
  [/leak/, 'leak'],
  [/latch|bolt|lock/, 'lock'],
  [/lqi|linkquality/, 'signal'],
  [/hvac_action/, 'thermostat'],
  [/threshold/, 'alert'],
  [/filter/, 'filter'],
  [/(^|_)in(put)?(_\d+)?$|_in_\d+$/, 'event'],
  [/battery_?state|charging/, 'battery-charging'],
  [/battery/, 'battery'],
  [/temp(erature)?\b|_temp_|flowtemp|returntemp/, 'thermometer'],
  [/humidity/, 'humidity'],
  [/co2|carbon_dioxide/, 'co2'],
  [/\bvoc\b|_voc/, 'molecule'],
  [/pressure/, 'pressure'],
  [/illuminance|_lux\b|brightness/, 'lux'],
  [/rssi|signal|_snr\b|_ping|latency/, 'signal'],
  [/wifi|ssid|bssid|_wlan/, 'wifi'],
  [/download|upload|throughput|bandwidth|_rx\b|_tx\b/, 'wifi'],
  // A satellite dish, never the dishwasher (caught above; the lookahead covers anything that slips past).
  [/starlink|dish(?!washer)/, 'dish'],
  [/\bip\b|_ip_|ip_address|ipv4|ipv6|mac_address|_uptime|firmware|version|connection_type|network/, 'router'],
  [/storage|disk|_sd_|memory|_ram\b/, 'storage'],
  [/cpu|processor|load_/, 'chip'],
  [/location|geocoded|_zone|distance|latitude|longitude|heading|altitude/, 'pin'],
  [/sim_?\d|carrier|cellular/, 'sim'],
  [/audio_output|volume|ringer/, 'volume'],
  [/steps|floors|activity|walking|pace/, 'motion'],
  [/focus|sleep|do_not_disturb/, 'moon'],
  [/last_update|last_seen|last_changed|_time\b|timestamp|_since|_at\b|next_|_last_/, 'clock'],
  [/solar|_pv|pv_|irradiance/, 'solar'],
  [/grid|import|export|tariff|_meter/, 'pylon'],
  [/inverter/, 'inverter'],
  [/power|_watt|_load\b/, 'bolt'],
  [/energy|kwh|consumption|production/, 'meter'],
  [/voltage|_volt/, 'voltage'],
  [/current|_amp/, 'current'],
  [/cost|price|rate_|balance|_gbp/, 'currency'],
  [/flow|pump/, 'pump'],
  [/water|tank|(^|_)orp(_|$)/, 'flask'],
  [/fan/, 'fan'],
  [/motion|occupan|presence/, 'motion'],
  [/door|gate/, 'door'],
  [/window/, 'window'],
  [/heat|boiler|radiator/, 'flame'],
  [/cool|_ac_|aircon/, 'snowflake'],
  [/camera|_review|detect|recording/, 'camera'],
  [/wind/, 'wind'],
  [/rain|precip/, 'rain'],
  [/uv_|_uv\b|sun_|sunrise|sunset|dawn|dusk|noon|midnight|solar_/, 'sun'],
  [/status|state|mode|error|fault|alarm/, 'event'],
];

const UNIT: Record<string, string> = {
  '°C': 'thermometer', '°F': 'thermometer', 'K': 'thermometer', 'W': 'bolt', 'kW': 'bolt', 'kWh': 'meter', 'Wh': 'meter', 'MWh': 'meter', 'V': 'voltage', 'mV': 'voltage', 'A': 'current', 'mA': 'current', 'Hz': 'current', 'dBm': 'signal', 'dB': 'signal', 'lx': 'lux', 'ppm': 'molecule', 'ppb': 'molecule', 'µg/m³': 'molecule', 'hPa': 'pressure', 'mbar': 'pressure', 'bar': 'pressure', 'psi': 'pressure', 'Mbit/s': 'wifi', 'kbit/s': 'wifi', 'MB/s': 'wifi', 'Gbit/s': 'wifi', 'GB': 'storage', 'MB': 'storage', 'TB': 'storage', 'GiB': 'storage', 's': 'timer', 'min': 'timer', 'h': 'timer', 'd': 'timer', 'ms': 'timer', '£': 'currency', 'GBP': 'currency', 'GBP/kWh': 'currency', '£/kWh': 'currency', 'p/kWh': 'currency', 'km': 'pin', 'm': 'pin', 'mi': 'pin', 'L/min': 'droplet', 'm³/h': 'droplet', 'L': 'droplet', 'm³': 'droplet',
};

const fromKeywords = (id: string): string | undefined => {
  const name = id.slice(id.indexOf('.') + 1);
  for (const [re, icon] of KEYWORDS) if (re.test(name)) return icon;
  return undefined;
};

const num = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
  return Number.isFinite(n) ? n : null;
};

// ---------------------------------------------------------------- state variants

const LIT = new Set<string>(LIGHT_ICONS);
const ON_VARIANT = new Set([...LIT, 'plug', 'socket', 'switch', 'tv', 'speaker', 'siren', 'vacuum', 'smoke', 'update']);
const PLAYING = new Set(['on', 'playing', 'paused', 'buffering', 'idle']);

function batteryLevel(pct: number): string {
  if (pct >= 88) return 'battery-100';
  if (pct >= 63) return 'battery-75';
  if (pct >= 38) return 'battery-50';
  if (pct >= 13) return 'battery-25';
  return 'battery-0';
}

/** Swaps a base glyph for its stateful variant. Explicit variants pass through unchanged. */
export function withState(base: string, meta: Pick<EntityMeta, 'domain' | 'deviceClass' | 'unit'> | undefined, s: EntityState | undefined): string {
  if (!s) return base;
  const st = s.s;
  if (st === 'unavailable' || st === 'unknown') return base;
  const domain = meta?.domain ?? '';
  const on = st === 'on';
  if (domain === 'media_player' && (base === 'tv' || base === 'speaker')) return PLAYING.has(st) && st !== 'idle' ? `${base}-on` : base;
  if (base === 'fan') return on ? 'fan-on' : base;
  if (base === 'toggle') return on ? 'toggle' : st === 'off' ? 'toggle-off' : base;
  if (ON_VARIANT.has(base)) return on ? `${base}-on` : base;

  const open = st === 'open' || st === 'opening' || (on && domain === 'binary_sensor');
  if (base === 'door' || base === 'window' || base === 'garage') return open ? `${base}-open` : base;

  if (base === 'lock' || base === 'unlock') {
    if (st === 'jammed') return 'lock-jammed';
    if (st === 'unlocked' || st === 'open' || st === 'opening' || st === 'unlocking') return 'unlock';
    if (st === 'locked' || st === 'locking') return 'lock';
    return base;
  }

  if (base === 'blind' || base === 'shutter' || base === 'curtain' || base === 'skylight') {
    const pos = num(s.a['current_position']);
    const pct = pos ?? (st === 'open' || st === 'opening' ? 100 : st === 'closed' || st === 'closing' ? 0 : null);
    if (pct === null) return base;
    if (pct >= 90) return `${base}-open`;
    if (pct <= 10) return base;
    return base === 'blind' || base === 'shutter' ? `${base}-50` : `${base}-open`;
  }

  if (base === 'battery' && domain !== 'binary_sensor') {
    const pct = num(st);
    if (pct !== null && (meta?.unit === '%' || meta?.deviceClass === 'battery' || meta?.unit == null)) return batteryLevel(pct);
    return base;
  }

  if (base === 'wifi' || base === 'signal') {
    let v = num(st);
    if (v === null) return base;
    if (meta?.unit === 'dBm' || v < 0) v = v >= -55 ? 100 : v >= -67 ? 70 : v >= -78 ? 40 : 10;
    const lvl = v >= 75 ? 4 : v >= 50 ? 3 : v >= 25 ? 2 : 1;
    if (base === 'wifi') return lvl >= 3 ? 'wifi' : `wifi-${lvl}`;
    return `signal-${lvl}`;
  }

  if (base === 'thermostat' || base === 'radiator' || base === 'ufh' || base === 'ac') {
    const action = typeof s.a['hvac_action'] === 'string' ? (s.a['hvac_action'] as string) : null;
    const heating = action ? action === 'heating' || action === 'preheating' : false;
    const cooling = action ? action === 'cooling' : false;
    const running = action ? action !== 'idle' && action !== 'off' : st !== 'off';
    if (base === 'thermostat') return heating ? 'thermostat-heat' : cooling ? 'thermostat-cool' : base;
    if (base === 'ac') return running && st !== 'off' ? 'ac-on' : base;
    return heating ? `${base}-on` : base;
  }

  if (base === 'shield' || base === 'shield-check' || base === 'shield-off') {
    if (domain !== 'alarm_control_panel') return base;
    if (st === 'disarmed') return 'shield-off';
    if (st === 'triggered') return 'shield-alert';
    if (st === 'arming' || st === 'pending') return 'shield-pending';
    if (st.startsWith('armed')) return 'shield-check';
    return base;
  }

  if (base === 'motion' || base === 'occupancy') return on ? `${base}-on` : base;
  if (base === 'leak-dry' || base === 'leak') return domain === 'binary_sensor' ? (on ? 'leak' : 'leak-dry') : base;
  if (base === 'bell' && domain === 'binary_sensor') return on ? 'bell-ring' : base;
  if (base === 'doorbell' && domain === 'binary_sensor') return on ? 'doorbell-ring' : base;
  return base;
}

// ---------------------------------------------------------------- iconFor

export interface IconPick {
  icon: string;
  source: IconSource;
}

type DeviceLike = Pick<Device, 'type' | 'icon'> | null | undefined;

/** Base icon and the step that chose it, before the state pass. */
export function pickBase(meta: EntityMeta | undefined, s?: EntityState, device?: DeviceLike): IconPick {
  const domain = meta?.domain ?? meta?.id?.split('.')[0] ?? '';
  const dc = meta?.deviceClass ?? null;
  const id = meta?.id ?? '';

  // 1. admin override
  const own = curated(meta?.icon);
  if (own) return { icon: own, source: 'override' };

  // Domains whose icon *is* the state (sky, alarm) beat whatever HA icon they carry.
  if (domain === 'weather') return { icon: (s && WEATHER[s.s]) ?? 'cloud-sun', source: 'domain' };
  if (domain === 'sun') return { icon: s?.s === 'below_horizon' ? 'moon' : 'sun', source: 'domain' };
  if (domain === 'alarm_control_panel') return { icon: 'shield', source: 'domain' };
  if (domain === 'binary_sensor' && dc && BINARY_DC[dc]) return { icon: BINARY_DC[dc][s?.s === 'on' ? 1 : 0], source: 'device_class' };

  // 2. HA icon
  const attrIcon = typeof s?.a['icon'] === 'string' ? (s.a['icon'] as string) : null;
  const fromMdi = mdiToIcon(meta?.icon) ?? mdiToIcon(attrIcon);
  const lightish = domain === 'light' || isLightSwitch(meta);
  if (fromMdi && !(lightish && fromMdi === 'bulb')) return { icon: fromMdi, source: 'mdi' };

  // 2b. lights: the fitting named in the entity's name ("Bedside left", "Row 3", "Floor lamp")
  if (lightish) {
    const kw = lightFromName(meta?.name, meta?.shortName);
    if (kw) return { icon: kw, source: 'name' };
    if (fromMdi) return { icon: fromMdi, source: 'mdi' };
    if (domain === 'switch') return { icon: 'bulb', source: 'domain' };
  }

  // 3. device_class (+ unit and id keywords for readings)
  if ((domain === 'sensor' || domain === 'number' || domain === 'input_number') && dc && SENSOR_DC[dc]) {
    return { icon: SENSOR_DC[dc], source: 'device_class' };
  }
  if (domain === 'cover' && dc && COVER_DC[dc]) return { icon: COVER_DC[dc], source: 'device_class' };
  if (domain === 'switch' && dc === 'outlet') return { icon: 'socket', source: 'device_class' };
  if (domain === 'media_player' && dc === 'tv') return { icon: 'tv', source: 'device_class' };
  if (domain === 'media_player' && dc === 'receiver') return { icon: 'tv-box', source: 'device_class' };
  if (domain === 'climate') {
    if (/zone_|_ac\b|ac$|parkair|aircon|daikin|mitsubishi/.test(id)) return { icon: 'ac', source: 'device_class' };
    if (/ufh|underfloor|floor/.test(id)) return { icon: 'ufh', source: 'device_class' };
    if (/trv|radiator/.test(id)) return { icon: 'radiator', source: 'device_class' };
  }
  if (domain === 'sensor' || domain === 'binary_sensor' || domain === 'number' || domain === 'select' || domain === 'switch' || domain === 'button') {
    const u = meta?.unit ?? '';
    if ((domain === 'sensor' || domain === 'number') && UNIT[u]) return { icon: UNIT[u], source: 'device_class' };
    const kw = fromKeywords(id);
    if (kw) return { icon: kw, source: 'device_class' };
  }

  // 4. domain
  if (DOMAIN[domain]) return { icon: DOMAIN[domain], source: 'domain' };

  // 5. device
  if (device) {
    const dIcon = device.icon ? (curated(device.icon) ?? (hasIcon(device.icon) ? device.icon : mdiToIcon(device.icon))) : undefined;
    if (dIcon) return { icon: dIcon, source: 'device' };
    if (device.type && device.type !== 'other') return { icon: deviceTypeIcon(device.type), source: 'device' };
  }
  if (WEAK_DOMAIN[domain]) return { icon: WEAK_DOMAIN[domain], source: 'domain_default' };

  // 6. generic
  return { icon: FALLBACK_ICON, source: 'fallback' };
}

/** A switch that drives lights or a lamp (switch.garage_lights, switch.living_room_side_lamp): grouped and drawn as a light (mirrors model.groupFor). */
function isLightSwitch(meta: EntityMeta | undefined): boolean {
  return !!meta && meta.domain === 'switch' && meta.deviceClass !== 'outlet' && meta.category == null
    && /\b(lights?|lamps?)\b/i.test(`${meta.name} ${meta.id.replace(/_/g, ' ')}`);
}

/** The icon for an entity in its current state. `device` lets a device type fill in when nothing else knows. */
export function iconFor(meta: EntityMeta | undefined, s?: EntityState, device?: DeviceLike): string {
  const { icon } = pickBase(meta, s, device);
  return withState(icon, meta, s);
}

/** Icon for a device: admin override (`eh:` or curated name or mdi), then its functional type. */
export function deviceIcon(device: DeviceLike): string {
  if (!device) return 'box';
  if (device.icon) {
    const own = curated(device.icon) ?? (hasIcon(device.icon) ? device.icon : mdiToIcon(device.icon));
    if (own) return own;
  }
  return deviceTypeIcon(device.type);
}

// ---------------------------------------------------------------- rooms

const ROOM_NAME: Array<[RegExp, string]> = [
  [/hot ?tub|spa\b|jacuzzi/, 'hot-tub'],
  [/garage|car ?port/, 'garage'],
  [/gym|peloton|fitness/, 'dumbbell'],
  [/dressing|wardrobe|closet/, 'shirt'],
  [/\bloo\b|toilet|\bwc\b|cloak/, 'toilet'],
  [/shower/, 'shower'],
  [/en.?suite|bath/, 'bath'],
  [/office|study|den\b/, 'desk'],
  [/dining/, 'dining'],
  [/kitchen/, 'kitchen'],
  [/stair|landing|hall/, 'stairs'],
  [/utility|laundry/, 'washer'],
  [/plant|boiler/, 'pipes'],
  [/loft|cupboard|store|attic/, 'box'],
  [/garden|patio|terrace|yard|drive/, 'garden'],
  [/cinema|media|tv room|snug/, 'tv'],
  [/server|network|comms/, 'server'],
  [/lounge|living|sitting|family/, 'sofa'],
  [/bed|nursery|guest/, 'bed'],
];

const ROOM_KIND: Record<string, string> = {
  bedroom: 'bed', bathroom: 'bath', kitchen: 'kitchen', living: 'sofa', circulation: 'stairs', outdoor: 'garden',
  utility: 'washer', plant: 'pipes',
};

export function roomIcon(room: Pick<Room, 'kind' | 'name' | 'icon'>): string {
  const m = mdiToIcon(room.icon ?? null);
  if (m) return m;
  const n = room.name.toLowerCase();
  for (const [re, icon] of ROOM_NAME) if (re.test(n)) return icon;
  return ROOM_KIND[room.kind] ?? 'home';
}
